// src/components/templates/FormatSample.jsx
//
// A small mockup of what a template's writing format looks like on the page —
// a screenplay's sluglines and centred character cues, a comic script's page
// and panel headings, an MLA header block, a planning sheet's label/value rows.
//
// Two sizes. `mini` fills a card's thumbnail; `full` is the larger one inside
// the expanded "Preview structure & sample". They share one definition per
// format so the thumbnail cannot drift from what the preview promises.
//
// **These are cosmetic.** The editor has no screenplay, comic or academic mode
// — `src/config/fonts.js` is a font-family picker and nothing more — so a
// sample here shows how the format is conventionally set, not a mode the app
// will put the author into. Real format support is its own piece of work; until
// it exists, nothing in the gallery should imply otherwise.
//
// Colours and fonts come from `templateTokens.js`, which maps onto the app's
// palette. The prototype's teal, rose and amber are not used.
import React from 'react';
import { Box } from '@mui/material';

import { SP, FONT, COURIER } from './templateTokens';

// The sample is one fictional scene reused across formats, so a reader
// comparing two cards is comparing the formatting rather than the writing.
const LINE = { marginBottom: '2px' };

// 8.5px, not the prototype's 6.5. At 6.5 the mockup was both illegible and too
// short to fill the thumbnail, so a card read as a failed image rather than as
// a preview of a page — which is the one job the thumbnail has.
const sampleSx = (variant) =>
  variant === 'mini'
    ? { fontSize: 8.5, lineHeight: 1.5, color: SP.black, opacity: 0.85 }
    : {
        fontSize: 11,
        lineHeight: 1.65,
        color: SP.black,
        background: SP.white,
        border: `1px solid ${SP.lineSoft}`,
        borderRadius: '4px',
        p: '16px 18px',
      };

/** Screenplay: uppercase slugline, centred character cue, narrow dialogue. */
function Screenplay({ mini }) {
  return (
    <Box sx={{ fontFamily: COURIER }}>
      <Box sx={{ ...LINE, fontWeight: 700, textTransform: 'uppercase' }}>
        INT. COFFEE SHOP - DAY
      </Box>
      <Box sx={{ ...LINE, mt: '8px' }}>
        Maya enters, scanning the room{mini ? '.' : '. She spots an empty chair across from a stranger reading a paperback.'}
      </Box>
      <Box sx={{ ...LINE, textTransform: 'uppercase', textAlign: 'center', mt: '12px', fontWeight: 700 }}>
        MAYA
      </Box>
      {!mini && (
        <Box sx={{ ...LINE, textAlign: 'center', fontStyle: 'italic', fontSize: '0.92em' }}>
          (hesitant)
        </Box>
      )}
      <Box sx={{ ...LINE, maxWidth: '62%', mx: 'auto' }}>Is this seat taken?</Box>
      {!mini && (
        <>
          <Box sx={{ ...LINE, textTransform: 'uppercase', textAlign: 'center', mt: '12px', fontWeight: 700 }}>
            TOM
          </Box>
          <Box sx={{ ...LINE, maxWidth: '62%', mx: 'auto' }}>
            Only if you don&rsquo;t mind spoilers.
          </Box>
        </>
      )}
    </Box>
  );
}

/** Stage play: character cue flush left, directions italic and indented. */
function StagePlay({ mini }) {
  const Cue = ({ children }) => (
    <Box sx={{ ...LINE, textTransform: 'uppercase', fontWeight: 700, mt: '10px' }}>{children}</Box>
  );
  const Direction = ({ children }) => (
    <Box sx={{ ...LINE, fontStyle: 'italic', color: SP.gray, ml: '18px' }}>{children}</Box>
  );
  const Speech = ({ children }) => <Box sx={{ ...LINE, ml: '18px' }}>{children}</Box>;

  return (
    <Box sx={{ fontFamily: FONT.serif }}>
      <Cue>MAYA</Cue>
      <Direction>(crossing to the window)</Direction>
      <Speech>I never thought I&rsquo;d see this house again.</Speech>
      {!mini && (
        <>
          <Cue>TOM</Cue>
          <Speech>Neither did I, if I&rsquo;m honest.</Speech>
          <Cue>MAYA</Cue>
          <Direction>(a long pause)</Direction>
          <Speech>Then why did you keep the key?</Speech>
        </>
      )}
    </Box>
  );
}

/** Comic script: page and panel headings, art description, lettering cues. */
function Comic({ mini }) {
  const Heading = ({ children, first }) => (
    <Box sx={{ ...LINE, fontWeight: 700, textTransform: 'uppercase', mt: first ? 0 : '8px' }}>
      {children}
    </Box>
  );
  const Art = ({ children }) => (
    <Box sx={{ ...LINE, fontStyle: 'italic', color: SP.gray, mt: '2px' }}>{children}</Box>
  );

  return (
    <Box sx={{ fontFamily: FONT.mono, fontSize: mini ? undefined : '10.5px' }}>
      <Heading first>PAGE 1</Heading>
      <Heading>PANEL 1</Heading>
      <Art>A cramped {mini ? '' : 'apartment '}kitchen. Steam rises from a kettle{mini ? '.' : ' on the stove.'}</Art>
      <Box sx={{ ...LINE, fontWeight: 700, textTransform: 'uppercase', mt: '6px' }}>MAYA (CAPTION):</Box>
      {!mini && (
        <>
          <Box sx={{ ...LINE, ml: '12px' }}>Some mornings start before you&rsquo;re ready for them.</Box>
          <Heading>PANEL 2</Heading>
          <Art>Close on a folded letter, unopened, on the counter.</Art>
          <Box sx={{ ...LINE, fontWeight: 700, textTransform: 'uppercase', mt: '6px' }}>MAYA (CAPTION):</Box>
          <Box sx={{ ...LINE, ml: '12px' }}>This one had been waiting three weeks.</Box>
        </>
      )}
    </Box>
  );
}

const citeSx = { color: SP.purple };
const footnoteMarkSx = { verticalAlign: 'super', fontSize: '0.75em', color: SP.goldDeep };

/** MLA: four-line header block top-left, centred title, (Author Page) citations. */
function AcademicMla({ mini }) {
  return (
    <Box sx={{ fontFamily: FONT.serif }}>
      <Box sx={{ ...LINE, fontFamily: FONT.sans, fontSize: '0.85em', color: SP.gray, lineHeight: 1.5 }}>
        Maya Chen<br />
        {mini ? 'Prof. Alvarez · Eng. 201' : <>Professor Alvarez<br />English 201</>}<br />
        14 August 2026
      </Box>
      <Box sx={{ ...LINE, textAlign: 'center', fontWeight: 600, mt: '10px' }}>
        The Weight of Memory{mini ? '' : ' in Coastal Fiction'}
      </Box>
      <Box sx={{ ...LINE, mt: '8px', textAlign: mini ? 'left' : 'justify' }}>
        Coastal fiction returns, again and again, to the {mini ? '' : 'image of the '}tide as memory
        {!mini && <Box component="span" sx={footnoteMarkSx}>1</Box>}
        {mini ? '' : ' — a force that erases and preserves in the same motion'}{' '}
        <Box component="span" sx={citeSx}>(Smith 42)</Box>.
      </Box>
      {!mini && (
        <Box sx={{ mt: '10px', pt: '6px', borderTop: `1px solid ${SP.lineSoft}`, fontSize: '0.8em', color: SP.gray }}>
          1. This reading builds on Smith&rsquo;s broader argument about landscape and recollection.
        </Box>
      )}
    </Box>
  );
}

/** APA: running head with page number, centred title, (Author, Year) citations. */
function AcademicApa({ mini }) {
  return (
    <Box sx={{ fontFamily: FONT.serif }}>
      <Box
        sx={{
          ...LINE,
          fontFamily: FONT.sans,
          fontSize: '0.75em',
          color: SP.gray,
          textTransform: 'uppercase',
          letterSpacing: '.05em',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>THE WEIGHT OF MEMORY</span>
        <span>2</span>
      </Box>
      <Box sx={{ ...LINE, textAlign: 'center', fontWeight: 600, mt: '10px' }}>
        The Weight of Memory{mini ? '' : ' in Coastal Fiction'}
      </Box>
      {!mini && (
        <Box sx={{ fontFamily: FONT.sans, fontSize: '0.85em', color: SP.gray, textAlign: 'center', mt: '4px' }}>
          Maya Chen<br />Department of English, Coastal University
        </Box>
      )}
      <Box sx={{ ...LINE, mt: '8px', textAlign: mini ? 'left' : 'justify' }}>
        Coastal narratives frequently return to {mini ? '…' : 'the tide as a figure for memory, a force that erases and preserves at once'}{' '}
        <Box component="span" sx={citeSx}>(Smith, 2020)</Box>.
      </Box>
    </Box>
  );
}

/** Chicago: numbered footnotes rather than parenthetical citations. */
function AcademicChicago({ mini }) {
  return (
    <Box sx={{ fontFamily: FONT.serif }}>
      <Box sx={{ ...LINE, textAlign: 'center', fontWeight: 600 }}>
        The Weight of Memory{mini ? '' : ' in Coastal Fiction'}
      </Box>
      <Box sx={{ ...LINE, mt: '8px', textAlign: mini ? 'left' : 'justify' }}>
        Coastal fiction returns{mini ? '' : ', again and again,'} to the tide as a figure for memory
        {mini ? '' : ', a force that erases and preserves in the same motion'}.
        <Box component="span" sx={footnoteMarkSx}>1</Box>
      </Box>
      <Box
        sx={{
          mt: '10px',
          pt: '6px',
          borderTop: `1px solid ${SP.lineSoft}`,
          fontSize: '0.8em',
          color: SP.gray,
        }}
      >
        1. James Smith, <i>Tidal Forms</i> (Boston: Harrow{mini ? '' : ' Press'}, 2020), 42.
      </Box>
    </Box>
  );
}

/** Planning sheet: label/value rows rather than running prose. */
function Sheet({ mini }) {
  const rows = mini
    ? [
        ['Name', 'Maya Chen'],
        ['Role', 'Protagonist'],
        ['Notes', 'Cautious, exacting…'],
      ]
    : [
        ['Name', 'Maya Chen'],
        ['Role', 'Protagonist'],
        ['Age', '29'],
        ['Appearance', 'Tall, deliberate movements, always in motion'],
        ['Motivation', 'To return home without becoming who she used to be'],
      ];

  return (
    <Box sx={{ fontFamily: FONT.sans }}>
      {rows.map(([label, value]) => (
        <Box
          key={label}
          sx={{ display: 'flex', gap: '8px', py: '4px', borderBottom: `1px dashed ${SP.lineSoft}` }}
        >
          <Box
            sx={{
              fontFamily: FONT.mono,
              fontSize: '0.82em',
              color: SP.goldDeep,
              textTransform: 'uppercase',
              letterSpacing: '.03em',
              width: '34%',
              flexShrink: 0,
            }}
          >
            {label}
          </Box>
          <Box sx={{ fontSize: '0.92em', color: SP.black }}>{value}</Box>
        </Box>
      ))}
    </Box>
  );
}

/** Prose: a chapter heading and an indented opening paragraph. */
function Prose({ mini }) {
  return (
    <Box sx={{ fontFamily: FONT.serif }}>
      <Box sx={{ ...LINE, fontWeight: 600, fontSize: '1.1em' }}>Chapter One</Box>
      {/* The mini runs on rather than trailing off after one line: the
          thumbnail is 118px and a single clipped sentence left most of it
          empty, which read as a broken card. */}
      <Box sx={{ ...LINE, mt: '8px', textIndent: '1.4em' }}>
        The tide came in early that year, ahead of the almanac
        {mini
          ? " and ahead of anyone's memory of it. Maya stood at the window and watched the water climb the seawall…"
          : " and ahead of anyone's memory of it. Maya stood at the window and watched the water climb the seawall, inch by patient inch, and thought: so it begins again."}
      </Box>
    </Box>
  );
}

/** Poetry: centred, italic, with a stanza break. */
function Poetry({ mini }) {
  return (
    <Box sx={{ fontFamily: FONT.serif, fontStyle: 'italic', textAlign: 'center' }}>
      <Box sx={LINE}>the tide keeps</Box>
      <Box sx={LINE}>what the shore forgets—</Box>
      <Box sx={LINE}>a key, a name,</Box>
      <Box sx={LINE}>the shape of a door</Box>
      {!mini && (
        <>
          <Box sx={{ height: '10px' }} />
          <Box sx={LINE}>left open</Box>
          <Box sx={LINE}>too long to close again</Box>
        </>
      )}
    </Box>
  );
}

const RENDERERS = {
  prose: Prose,
  poetry: Poetry,
  screenplay: Screenplay,
  stageplay: StagePlay,
  comic: Comic,
  'academic-mla': AcademicMla,
  'academic-apa': AcademicApa,
  'academic-chicago': AcademicChicago,
  sheet: Sheet,
};

export default function FormatSample({ format, variant = 'mini' }) {
  // An unknown format falls back to prose rather than rendering nothing: the
  // catalogue is seeded data, and a card with an empty thumbnail reads as a
  // broken page rather than as a template whose sample is missing.
  const Renderer = RENDERERS[format] || Prose;

  return (
    <Box sx={sampleSx(variant)} data-testid={`format-sample-${variant}`}>
      <Renderer mini={variant === 'mini'} />
    </Box>
  );
}
